import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Chip,
    Button,
    TextField,
    Stack,
    CircularProgress,
    Alert,
    Divider,
    IconButton,
    ToggleButtonGroup,
    ToggleButton,
    Link,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    ButtonGroup,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { format } from 'date-fns';
import { doc, getDoc, updateDoc, Timestamp, collection, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { CRMEntry, CRMNote, CRMStage, CalendarEvent } from '../../../models/CRMEntry';
import LinkIcon from '@mui/icons-material/Link';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import HistoryIcon from '@mui/icons-material/History';
import { MeetingDialog } from './MeetingDialog';
import moment from 'moment-timezone';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Business } from '../../../models/Business';

const STAGES: CRMStage[] = [
    'New Lead',
    'Follow-Up Scheduled',
    'Proposal Sent',
    'Contract Sent',
    'Active Order',
    'Closed Won',
    'Closed Lost',
];

const STAGES_WITH_CONTRACTS: CRMStage[] = [
    'Proposal Sent',
    'Contract Sent',
    'Active Order',
    'Closed Won'
];

interface User {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
}

// Get user's timezone
const userTimezone = moment.tz.guess();

export const CRMDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [entry, setEntry] = useState<CRMEntry | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [newNote, setNewNote] = useState('');
    const [users, setUsers] = useState<User[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editedValues, setEditedValues] = useState({
        contactFirstName: '',
        contactLastName: '',
        contactEmail: '',
        contactPhone: '',
        assignedTo: ''
    });
    const [newEvent, setNewEvent] = useState({
        title: '',
        date: '',
        time: '',
        duration: 30, // default to 30 minutes
        location: ''
    });
    const [dateError, setDateError] = useState<string | null>(null);
    const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
    const [clientInfo, setClientInfo] = useState<{ name: string } | null>(null);
    const [business, setBusiness] = useState<Business | null>(null);
    const [editingDriveLink, setEditingDriveLink] = useState(false);
    const [driveLinkValue, setDriveLinkValue] = useState('');
    const [driveLinkError, setDriveLinkError] = useState<string | null>(null);

    const parseNotes = (notesStr: string | null): CRMNote[] => {
        if (!notesStr) return [];
        try {
            return JSON.parse(notesStr);
        } catch (err) {
            // If the notes field isn't valid JSON, treat it as a single note
            return [{
                id: '1',
                text: notesStr,
                createdAt: new Date()
            }];
        }
    };

    useEffect(() => {
        const loadCRMEntry = async () => {
            if (!id) return;
            try {
                const docRef = doc(db, 'crmEntries', id);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    console.log('Loaded CRM entry:', data); // Debug log
                    setEntry({ id: docSnap.id, ...data } as CRMEntry);
                } else {
                    setError('CRM entry not found');
                }
            } catch (err) {
                console.error('Error loading CRM entry:', err);
                setError('Failed to load CRM entry');
            } finally {
                setLoading(false);
            }
        };

        loadCRMEntry();
    }, [id]);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const usersCollection = collection(db, 'users');
                const usersSnapshot = await getDocs(usersCollection);
                const usersList = usersSnapshot.docs.map(doc => ({
                    id: doc.id,
                    email: doc.data().email,
                    firstName: doc.data().firstName,
                    lastName: doc.data().lastName
                }));
                setUsers(usersList);
            } catch (err) {
                console.error('Error fetching users:', err);
            }
        };

        fetchUsers();
    }, []);

    useEffect(() => {
        if (entry) {
            setEditedValues({
                contactFirstName: entry.contactFirstName || '',
                contactLastName: entry.contactLastName || '',
                contactEmail: entry.contactEmail || '',
                contactPhone: entry.contactPhone || '',
                assignedTo: entry.assignedTo || ''
            });
        }
    }, [entry]);

    useEffect(() => {
        const loadClientInfo = async () => {
            if (!entry?.clientId) return;
            try {
                const clientDoc = await getDoc(doc(db, 'clients', entry.clientId));
                if (clientDoc.exists()) {
                    setClientInfo({
                        name: clientDoc.data().companyName
                    });
                }
            } catch (err) {
                console.error('Error loading client info:', err);
            }
        };
        loadClientInfo();
    }, [entry?.clientId]);

    useEffect(() => {
        const fetchBusiness = async () => {
            if (entry?.businessId) {
                try {
                    const businessDoc = doc(db, 'businesses', entry.businessId);
                    const businessSnap = await getDoc(businessDoc);
                    if (businessSnap.exists()) {
                        const businessData = businessSnap.data();
                        setBusiness({ id: businessSnap.id, ...businessData } as Business);
                        setDriveLinkValue(businessData.googleDriveLink || '');
                    } else {
                        setBusiness(null);
                        setDriveLinkValue('');
                    }
                } catch (err) {
                    setBusiness(null);
                    setDriveLinkValue('');
                }
            } else {
                setBusiness(null);
                setDriveLinkValue('');
            }
        };
        fetchBusiness();
    }, [entry?.businessId]);

    const handleAddNote = async () => {
        if (!entry || !newNote.trim()) return;

        try {
            const note: CRMNote = {
                id: Math.random().toString(36).substr(2, 9),
                text: newNote.trim(),
                createdAt: new Date(),
            };

            const existingNotes = parseNotes(entry.notes);
            const updatedNotes = [...existingNotes, note];

            const docRef = doc(db, 'crmEntries', entry.id);
            await updateDoc(docRef, {
                notes: JSON.stringify(updatedNotes),
                lastActivityAt: Timestamp.now(),
            });

            // Update local state
            setEntry(prev => {
                if (!prev) return null;
                return {
                    ...prev,
                    notes: JSON.stringify(updatedNotes)
                };
            });
            setNewNote('');
        } catch (err) {
            console.error('Error adding note:', err);
            setError('Failed to add note');
        }
    };

    const handleConvertToOrder = async () => {
        if (!entry) return;
        // TODO: Implement order conversion logic
        console.log('Converting to order:', entry.id);
    };

    const handleBack = () => {
        navigate('..');
    };

    const handleStageChange = async (event: React.MouseEvent<HTMLElement>, newStage: CRMStage | null) => {
        if (!entry || !newStage || newStage === entry.stage) return;

        try {
            const docRef = doc(db, 'crmEntries', entry.id);
            await updateDoc(docRef, {
                stage: newStage,
                lastActivityAt: Timestamp.now(),
            });

            // Update local state
            setEntry(prev => {
                if (!prev) return null;
                return {
                    ...prev,
                    stage: newStage,
                    lastActivityAt: Timestamp.now() as any, // Type cast to match CRMEntry
                };
            });
        } catch (err) {
            console.error('Error updating stage:', err);
            setError('Failed to update stage');
        }
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleSave = async () => {
        if (!entry) return;

        try {
            const docRef = doc(db, 'crmEntries', entry.id);
            await updateDoc(docRef, {
                contactFirstName: editedValues.contactFirstName,
                contactLastName: editedValues.contactLastName,
                contactEmail: editedValues.contactEmail,
                contactPhone: editedValues.contactPhone,
                assignedTo: editedValues.assignedTo,
                lastActivityAt: Timestamp.now()
            });

            // Update local state
            setEntry(prev => {
                if (!prev) return null;
                return {
                    ...prev,
                    contactFirstName: editedValues.contactFirstName,
                    contactLastName: editedValues.contactLastName,
                    contactEmail: editedValues.contactEmail,
                    contactPhone: editedValues.contactPhone,
                    assignedTo: editedValues.assignedTo,
                    lastActivityAt: Timestamp.now() as any
                };
            });

            setIsEditing(false);
        } catch (err) {
            console.error('Error updating contact info:', err);
            setError('Failed to update contact information');
        }
    };

    const handleCancel = () => {
        if (entry) {
            setEditedValues({
                contactFirstName: entry.contactFirstName || '',
                contactLastName: entry.contactLastName || '',
                contactEmail: entry.contactEmail || '',
                contactPhone: entry.contactPhone || '',
                assignedTo: entry.assignedTo || ''
            });
        }
        setIsEditing(false);
    };

    const validateDateTime = (date: string, time: string): boolean => {
        if (!date || !time) return false;

        const meetingDate = new Date(`${date}T${time}`);
        const now = new Date();

        // Only block if the meeting is in the past (date and time)
        if (meetingDate < now) {
            setDateError('Meeting cannot be scheduled in the past');
            return false;
        }

        setDateError(null);
        return true;
    };

    const handleAddEvent = async () => {
        if (!entry || !newEvent.title || !newEvent.date || !newEvent.time) return;

        if (!validateDateTime(newEvent.date, newEvent.time)) {
            return;
        }

        try {
            const startDate = new Date(`${newEvent.date}T${newEvent.time}`);
            const endDate = new Date(startDate.getTime() + newEvent.duration * 60000);

            const event: CalendarEvent = {
                id: Math.random().toString(36).substr(2, 9),
                title: newEvent.title,
                startTime: Timestamp.fromDate(startDate),
                endTime: Timestamp.fromDate(endDate),
                ...(newEvent.location ? { location: newEvent.location } : {})
            };

            const updatedEvents = [...(entry.calendarEvents || []), event];
            const newStage = entry.stage === 'New Lead' ? 'Follow-Up Scheduled' : entry.stage;

            const updateData = {
                calendarEvents: updatedEvents,
                lastActivityAt: Timestamp.now(),
                stage: newStage
            };

            const docRef = doc(db, 'crmEntries', entry.id);
            await updateDoc(docRef, updateData);

            setEntry(prev => {
                if (!prev) return null;
                return {
                    ...prev,
                    calendarEvents: updatedEvents,
                    lastActivityAt: Timestamp.now() as any,
                    stage: newStage
                };
            });

            setNewEvent({
                title: '',
                date: '',
                time: '',
                duration: 30,
                location: ''
            });
            setDateError(null);
            setError(null);
        } catch (err) {
            console.error('Error adding event:', err);
            setError('Failed to add event. Please try again.');
        }
    };

    const handleSaveDriveLink = async () => {
        if (!business) return;
        try {
            const businessDoc = doc(db, 'businesses', business.id);
            await updateDoc(businessDoc, { googleDriveLink: driveLinkValue });
            setBusiness(prev => prev ? { ...prev, googleDriveLink: driveLinkValue } : prev);
            setEditingDriveLink(false);
            setDriveLinkError(null);
        } catch (err) {
            setDriveLinkError('Failed to update Google Drive link');
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error || !entry) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {error || 'Entry not found'}
            </Alert>
        );
    }

    const sortedEvents = entry.calendarEvents?.slice()
        .sort((a, b) => a.startTime.toMillis() - b.startTime.toMillis()) || [];
    const upcomingEvents = sortedEvents.filter(event =>
        event.startTime.toMillis() > Date.now()
    );

    return (
        <Box sx={{ p: 3, maxWidth: '1200px', margin: '0 auto' }}>
            {/* Current Stage Banner */}
            <Paper
                sx={{
                    p: 2,
                    mb: 3,
                    bgcolor: (theme) => {
                        switch (entry.stage) {
                            case 'Closed Won':
                                return theme.palette.success.light;
                            case 'Closed Lost':
                                return theme.palette.error.light;
                            default:
                                return theme.palette.primary.light;
                        }
                    },
                    color: (theme) => {
                        switch (entry.stage) {
                            case 'Closed Won':
                                return theme.palette.success.contrastText;
                            case 'Closed Lost':
                                return theme.palette.error.contrastText;
                            default:
                                return theme.palette.primary.contrastText;
                        }
                    }
                }}
            >
                <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="h6" component="div">
                        Current Stage: {entry.stage}
                    </Typography>
                    <Chip
                        label={entry.status}
                        sx={{
                            bgcolor: 'background.paper',
                            '& .MuiChip-label': {
                                color: 'text.primary'
                            }
                        }}
                    />
                </Stack>
            </Paper>

            {/* Back Button */}
            <IconButton
                onClick={handleBack}
                sx={{ mb: 2 }}
                aria-label="back to overview"
            >
                <ArrowBackIcon />
            </IconButton>

            <Grid container spacing={3}>
                {/* Contact Info Card - Left Column */}
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="h6">
                                Contact Information
                            </Typography>
                            {isEditing ? (
                                <Stack direction="row" spacing={1}>
                                    <Button size="small" onClick={handleSave} variant="contained">
                                        Save
                                    </Button>
                                    <Button size="small" onClick={handleCancel}>
                                        Cancel
                                    </Button>
                                </Stack>
                            ) : (
                                <Button size="small" onClick={handleEdit}>
                                    Edit
                                </Button>
                            )}
                        </Stack>
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <PersonIcon color="action" />
                                {isEditing ? (
                                    <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="First Name"
                                            value={editedValues.contactFirstName}
                                            onChange={(e) => setEditedValues(prev => ({ ...prev, contactFirstName: e.target.value }))}
                                            placeholder="Enter first name"
                                        />
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Last Name"
                                            value={editedValues.contactLastName}
                                            onChange={(e) => setEditedValues(prev => ({ ...prev, contactLastName: e.target.value }))}
                                            placeholder="Enter last name"
                                        />
                                    </Stack>
                                ) : (
                                    <Typography>
                                        {entry.contactFirstName || entry.contactLastName ? (
                                            `${entry.contactFirstName || ''} ${entry.contactLastName || ''}`
                                        ) : (
                                            <Typography color="text.secondary">No contact name provided</Typography>
                                        )}
                                    </Typography>
                                )}
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <EmailIcon color="action" />
                                {isEditing ? (
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={editedValues.contactEmail}
                                        onChange={(e) => setEditedValues(prev => ({ ...prev, contactEmail: e.target.value }))}
                                        placeholder="Enter email"
                                    />
                                ) : (
                                    <Typography>
                                        {entry.contactEmail ? (
                                            <Link href={`mailto:${entry.contactEmail}`} underline="hover">
                                                {entry.contactEmail}
                                            </Link>
                                        ) : (
                                            <Typography color="text.secondary">No email provided</Typography>
                                        )}
                                    </Typography>
                                )}
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <PhoneIcon color="action" />
                                {isEditing ? (
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={editedValues.contactPhone}
                                        onChange={(e) => setEditedValues(prev => ({ ...prev, contactPhone: e.target.value }))}
                                        placeholder="Enter phone number"
                                    />
                                ) : (
                                    <Typography>
                                        {entry.contactPhone ? (
                                            <Link href={`tel:${entry.contactPhone}`} underline="hover">
                                                {entry.contactPhone}
                                            </Link>
                                        ) : (
                                            <Typography color="text.secondary">No phone number provided</Typography>
                                        )}
                                    </Typography>
                                )}
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <PersonIcon color="action" />
                                {isEditing ? (
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="assigned-to-label">Assigned To</InputLabel>
                                        <Select
                                            labelId="assigned-to-label"
                                            value={editedValues.assignedTo}
                                            onChange={(e) => setEditedValues(prev => ({ ...prev, assignedTo: e.target.value }))}
                                            label="Assigned To"
                                        >
                                            <MenuItem value="">
                                                <em>None</em>
                                            </MenuItem>
                                            {users.map(user => (
                                                <MenuItem key={user.id} value={user.email}>
                                                    {`${user.firstName} ${user.lastName} (${user.email})`}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ) : (
                                    <Typography>
                                        {entry.assignedTo ? (
                                            users.find(u => u.email === entry.assignedTo) ?
                                                `${users.find(u => u.email === entry.assignedTo)?.firstName} ${users.find(u => u.email === entry.assignedTo)?.lastName} (${entry.assignedTo})` :
                                                entry.assignedTo
                                        ) : (
                                            <Typography color="text.secondary">Not assigned</Typography>
                                        )}
                                    </Typography>
                                )}
                            </Stack>
                            {entry.website && (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <LinkIcon color="action" />
                                    <Typography>
                                        <Link href={entry.website} target="_blank" rel="noopener noreferrer" underline="hover">
                                            {new URL(entry.website).hostname}
                                        </Link>
                                    </Typography>
                                </Stack>
                            )}
                            {entry.clientId && (
                                <Paper sx={{ p: 3, mb: 3 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Client Information
                                    </Typography>
                                    <Stack spacing={2}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <BusinessIcon color="primary" />
                                            <Box>
                                                <Typography variant="subtitle1">
                                                    {clientInfo?.name || 'Loading client...'}
                                                </Typography>
                                                <Stack direction="row" spacing={2}>
                                                    <Link
                                                        component={RouterLink}
                                                        to={`/clients/${entry.clientId}`}
                                                        underline="hover"
                                                        color="primary"
                                                    >
                                                        View Client Profile
                                                    </Link>
                                                    <Link
                                                        component={RouterLink}
                                                        to={`/clients/${entry.clientId}/entries`}
                                                        underline="hover"
                                                        color="primary"
                                                    >
                                                        View Client History
                                                    </Link>
                                                </Stack>
                                            </Box>
                                        </Stack>
                                    </Stack>
                                </Paper>
                            )}
                            {business && (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <LinkIcon color="action" />
                                    {editingDriveLink ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                value={driveLinkValue}
                                                onChange={e => setDriveLinkValue(e.target.value)}
                                                placeholder="Paste Google Drive folder URL"
                                                error={!!driveLinkError}
                                                helperText={driveLinkError}
                                            />
                                            <IconButton onClick={handleSaveDriveLink} color="primary">
                                                <SaveIcon />
                                            </IconButton>
                                        </Box>
                                    ) : business.googleDriveLink ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                            <Link
                                                href={business.googleDriveLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                underline="hover"
                                                sx={{
                                                    wordBreak: 'break-all',
                                                    maxWidth: '100%',
                                                    display: 'inline-block',
                                                    whiteSpace: 'pre-line',
                                                }}
                                            >
                                                {business.googleDriveLink}
                                            </Link>
                                            <IconButton onClick={() => setEditingDriveLink(true)} size="small">
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <Button onClick={() => setEditingDriveLink(true)} size="small" variant="outlined">
                                            Add Google Drive Link
                                        </Button>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    </Paper>
                </Grid>

                {/* Main Content - Right Column */}
                <Grid item xs={12} md={8}>
                    {/* Header with Company Name and Stage Badge */}
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12}>
                                <Typography variant="h4" gutterBottom>
                                    {entry.companyName}
                                </Typography>
                                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                                    <Chip
                                        label={entry.stage}
                                        color={
                                            entry.stage === 'Closed Won'
                                                ? 'success'
                                                : entry.stage === 'Closed Lost'
                                                    ? 'error'
                                                    : 'primary'
                                        }
                                    />
                                    <Chip label={entry.status} color="secondary" />
                                    {entry.value && (
                                        <Chip label={`$${entry.value.toLocaleString()}`} color="success" />
                                    )}
                                </Stack>
                            </Grid>
                            {STAGES_WITH_CONTRACTS.includes(entry.stage) && (
                                <Grid item xs={12}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleConvertToOrder}
                                    >
                                        Convert to Order
                                    </Button>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>

                    {/* Deal Info */}
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Deal Info
                        </Typography>
                        <Stack spacing={2}>
                            <Typography>
                                <strong>Created:</strong>{' '}
                                {format(entry.createdAt.toDate(), 'MMM d, yyyy')}
                            </Typography>
                            {entry.expectedCloseDate && (
                                <Typography>
                                    <strong>Expected Close:</strong>{' '}
                                    {format(entry.expectedCloseDate.toDate(), 'MMM d, yyyy')}
                                </Typography>
                            )}
                            {entry.lastActivityAt && (
                                <Typography>
                                    <strong>Last Activity:</strong>{' '}
                                    {format(entry.lastActivityAt.toDate(), 'MMM d, yyyy')}
                                </Typography>
                            )}
                        </Stack>
                    </Paper>

                    {/* Notes Section */}
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Notes & Updates
                        </Typography>
                        <Box sx={{ mb: 3 }}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                placeholder="Add a note..."
                                value={newNote}
                                onChange={(e) => setNewNote(e.target.value)}
                                sx={{ mb: 2 }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleAddNote}
                                disabled={!newNote.trim()}
                            >
                                Add Note
                            </Button>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Stack spacing={2}>
                            {entry.notes && parseNotes(entry.notes).map((note: CRMNote) => (
                                <Paper key={note.id} sx={{ p: 2, bgcolor: 'background.default' }}>
                                    <Typography>{note.text}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {format(new Date(note.createdAt), 'MMM d, yyyy h:mm a')}
                                    </Typography>
                                </Paper>
                            ))}
                        </Stack>
                    </Paper>

                    {/* Contracts and Payments Sections */}
                    {STAGES_WITH_CONTRACTS.includes(entry.stage) ? (
                        <>
                            {/* Contracts Section */}
                            <Paper sx={{ p: 3, mb: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    Contracts
                                </Typography>
                                <Typography color="text.secondary">
                                    Contract management coming soon...
                                </Typography>
                            </Paper>

                            {/* Payments Section */}
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    Payments
                                </Typography>
                                <Typography color="text.secondary">
                                    Payment tracking coming soon...
                                </Typography>
                            </Paper>
                        </>
                    ) : (
                        <Paper
                            sx={{
                                p: 3,
                                bgcolor: 'grey.100',
                                textAlign: 'center',
                                color: 'text.secondary'
                            }}
                        >
                            <Typography>
                                Contracts and payments will appear once a proposal is sent.
                            </Typography>
                        </Paper>
                    )}

                    {/* Meeting Scheduler Section */}
                    <Paper sx={{ p: 3, mt: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Schedule Meeting
                        </Typography>
                        {(error || dateError) && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error || dateError}
                            </Alert>
                        )}
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Title"
                                    value={newEvent.title}
                                    onChange={(e) => setNewEvent(prev => ({ ...prev, title: e.target.value }))}
                                    error={!newEvent.title}
                                    helperText={!newEvent.title ? 'Title is required' : ''}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Date"
                                    type="date"
                                    value={newEvent.date}
                                    onChange={(e) => setNewEvent(prev => ({ ...prev, date: e.target.value }))}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{
                                        min: undefined // Remove min restriction so user can pick today
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Time"
                                    type="time"
                                    value={newEvent.time}
                                    onChange={(e) => setNewEvent(prev => ({ ...prev, time: e.target.value }))}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{
                                        step: 300 // 5 minutes in seconds
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Duration
                                </Typography>
                                <ButtonGroup variant="outlined" aria-label="meeting duration">
                                    {[15, 30, 45, 60].map((mins) => (
                                        <Button
                                            key={mins}
                                            onClick={() => setNewEvent(prev => ({ ...prev, duration: mins }))}
                                            variant={newEvent.duration === mins ? 'contained' : 'outlined'}
                                        >
                                            {mins === 60 ? '1 hour' : `${mins} min`}
                                        </Button>
                                    ))}
                                </ButtonGroup>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Location (Optional)"
                                    value={newEvent.location}
                                    onChange={(e) => setNewEvent(prev => ({ ...prev, location: e.target.value }))}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    variant="contained"
                                    onClick={handleAddEvent}
                                    disabled={!newEvent.title || !newEvent.date || !newEvent.time || Boolean(dateError)}
                                >
                                    Schedule Meeting
                                </Button>
                                {dateError && (
                                    <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>
                                        {dateError}
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>

                        {upcomingEvents.length > 0 && (
                            <>
                                <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                                    Upcoming Meetings
                                </Typography>
                                <Stack spacing={2}>
                                    {upcomingEvents.map(event => (
                                        <Paper
                                            key={event.id}
                                            sx={{
                                                p: 2,
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    bgcolor: 'action.hover'
                                                }
                                            }}
                                            onClick={() => setSelectedEvent(event)}
                                        >
                                            <Typography variant="subtitle1" gutterBottom>
                                                {event.title}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {format(event.startTime.toDate(), 'PPp')} - {format(event.endTime.toDate(), 'p')}
                                            </Typography>
                                            {event.location && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Location: {event.location}
                                                </Typography>
                                            )}
                                        </Paper>
                                    ))}
                                </Stack>
                            </>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Meeting Dialog */}
            {selectedEvent && entry && (
                <MeetingDialog
                    open={true}
                    onClose={() => setSelectedEvent(null)}
                    event={selectedEvent}
                    crmEntry={entry}
                    onEventUpdate={() => {
                        setSelectedEvent(null);
                        // Refresh the entry to get updated events
                        const docRef = doc(db, 'crmEntries', entry.id);
                        getDoc(docRef).then((docSnap) => {
                            if (docSnap.exists()) {
                                setEntry({ id: docSnap.id, ...docSnap.data() } as CRMEntry);
                            }
                        });
                    }}
                    userTimezone={userTimezone}
                />
            )}
        </Box>
    );
}; 